// oxlint-disable no-explicit-any Copied from zod-form-data
// We'll improve this at some point, but it's taken directly from:
// https://github.com/airjp73/rvf/blob/main/packages/zod-form-data/src/helpers.ts
// This has been updated for zod 4
import * as z from 'zod';

type InputType<DefaultType extends z.ZodTypeAny> = {
  (): DefaultType;
  <ProvidedType extends z.ZodTypeAny>(schema: ProvidedType): ProvidedType;
};

const stripEmpty = z.literal('').transform(() => undefined);

const preprocessIfValid = (schema: z.ZodTypeAny) => (val: unknown) => {
  const result = schema.safeParse(val);

  return result.success ? result.data : val;
};

/**
 * Transforms any empty strings to `undefined` before validating.
 * This makes it so empty strings will fail required checks,
 * allowing you to use `optional` for optional fields instead of `nonempty` for required fields.
 * If you call `zfd.text` with no arguments, it will assume the field is a required string by default.
 * If you want to customize the schema, you can pass that as an argument.
 */
export const text: InputType<z.ZodString> = (schema = z.string()) =>
  z.preprocess(preprocessIfValid(stripEmpty), schema) as any;

/**
 * Coerces numerical strings to numbers transforms empty strings to `undefined` before validating.
 * If you call `zfd.number` with no arguments,
 * it will assume the field is a required number by default.
 * If you want to customize the schema, you can pass that as an argument.
 */
export const numeric: InputType<z.ZodNumber> = (schema = z.number()) =>
  z.preprocess(
    preprocessIfValid(
      z.union([
        stripEmpty,
        z
          .string()
          .transform(val => Number(val))
          .refine(val => !Number.isNaN(val)),
      ]),
    ),
    schema,
  ) as any;

type CheckboxOpts = {
  trueValue?: string;
};

/**
 * Turns the value from a checkbox field into a boolean,
 * but does not require the checkbox to be checked.
 * For checkboxes with a `value` attribute, you can pass that as the `trueValue` option.
 *
 * @example
 * ```ts
 * const schema = zfd.formData({
 *   defaultCheckbox: zfd.checkbox(),
 *   checkboxWithValue: zfd.checkbox({ trueValue: "true" }),
 *   mustBeTrue: zfd
 *     .checkbox()
 *     .refine((val) => val, "Please check this box"),
 *   });
 * });
 * ```
 */
export const checkbox = ({ trueValue = 'on' }: CheckboxOpts = {}) =>
  // An unchecked box sends no entry at all, so the key is absent from the parsed form data. Zod 4
  // decides key optionality from the schema wrapper rather than whether it accepts `undefined`, so
  // this has to be `.optional()` or every unchecked box fails with "expected nonoptional".
  z
    .union([z.literal(trueValue), z.undefined()])
    .optional()
    .transform(value => value === trueValue);

export const file: InputType<z.ZodType<File>> = (schema = z.instanceof(File)) =>
  z.preprocess(val => {
    //Empty File object on no user input, so convert to undefined
    return val instanceof File && val.size === 0 ? undefined : val;
  }, schema) as any;

/**
 * Preprocesses a field where you expect multiple values could be present for the same field name
 * and transforms the value of that field to always be an array.
 * If you don't provide a schema, it will assume the field is an array of zfd.text fields
 * and will not require any values to be present.
 */
export const repeatable: InputType<z.ZodArray<any>> = (schema = z.array(text())) => {
  return z.preprocess(val => {
    if (Array.isArray(val)) {
      return val;
    }
    if (val === undefined) {
      return [];
    }

    return [val];
  }, schema) as any;
};

/**
 * A convenience wrapper for repeatable.
 * Instead of passing the schema for an entire array, you pass in the schema for the item type.
 */
export const repeatableOfType = <T extends z.ZodTypeAny>(schema: T): z.ZodArray<T> =>
  repeatable(z.array(schema));

export const optionalNumeric = () =>
  zfd.numeric(z.number().nullable().optional()).transform(val => (val === null ? undefined : val));

export const zfd = {
  text,
  numeric,
  checkbox,
  file,
  repeatable,
  repeatableOfType,
  optionalNumeric,
};
